import './chat.web.css';

import { useCallback, useRef, useState } from 'react';
import { ActivityIndicator, Pressable } from 'react-native';
import { useRouter } from 'expo-router';

import { Menu, MenuItem, Text, anchorAtPointer, anchorBelowElement } from '@/components/ui';
import type { MenuAnchor } from '@/components/ui';
import {
  deleteConversation,
  forkConversation,
} from '@/lib/chat/api';
import {
  archiveChat,
  isChatPinned,
  togglePinChat,
  unarchiveChat,
} from '@/lib/chat/conversation-prefs';
import { notifyChatConversationsChanged } from '@/lib/chat/events';
import { useConversationStreaming } from '@/lib/chat/stream-registry';
import type { Conversation } from '@/lib/chat/types';
import { cn } from '@/lib/design-system/cn';

type ChatConversationRowProps = {
  conversation: Conversation;
  active: boolean;
  archived?: boolean;
  onOpen: (id: string) => void;
  onChanged: () => void;
  onRenameRequest: (conversation: Conversation) => void;
};

function conversationLabel(conversation: Conversation): string {
  return conversation.title?.trim() || 'New conversation';
}

export function ChatConversationRow({
  conversation,
  active,
  archived = false,
  onOpen,
  onChanged,
  onRenameRequest,
}: ChatConversationRowProps) {
  const router = useRouter();
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchor | null>(null);
  const pinned = isChatPinned(conversation.id);
  const running = useConversationStreaming(conversation.id);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMenuAnchor(null);
  }, []);

  function openMenuAtButton() {
    const button = menuButtonRef.current;
    if (!button) {
      return;
    }
    setMenuAnchor(anchorBelowElement(button));
    setMenuOpen(true);
  }

  function openMenuAtPointer(clientX: number, clientY: number) {
    setMenuAnchor(anchorAtPointer(clientX, clientY));
    setMenuOpen(true);
  }

  function onRename() {
    closeMenu();
    onRenameRequest(conversation);
  }

  async function onFork() {
    closeMenu();
    const forked = await forkConversation(conversation.id);
    notifyChatConversationsChanged();
    router.push(`/chat/${forked.id}`);
    onChanged();
  }

  async function onDelete() {
    closeMenu();
    if (!window.confirm(`Delete "${conversationLabel(conversation)}"?`)) {
      return;
    }
    await deleteConversation(conversation.id);
    notifyChatConversationsChanged();
    onChanged();
    if (active) {
      router.push('/chat');
    }
  }

  function onPin() {
    closeMenu();
    togglePinChat(conversation.id);
    onChanged();
  }

  function onArchive() {
    closeMenu();
    archiveChat(conversation.id);
    notifyChatConversationsChanged();
    onChanged();
    if (active) {
      router.push('/chat');
    }
  }

  function onRestore() {
    closeMenu();
    unarchiveChat(conversation.id);
    notifyChatConversationsChanged();
    onChanged();
  }

  return (
    <>
      <div
        className={cn(
          'chat-conversation-row group flex items-center rounded px-1.5 py-1',
          active && !running ? 'bg-surface' : '',
          running ? 'chat-conversation-row-running' : '',
          menuOpen ? 'chat-conversation-row-menu-open' : '',
        )}
        aria-busy={running}
        title={running ? 'Agent is working' : undefined}
        onContextMenu={(event) => {
          event.preventDefault();
          openMenuAtPointer(event.clientX, event.clientY);
        }}
      >
        <Pressable className="min-w-0 flex-1 py-0.5" onPress={() => onOpen(conversation.id)}>
          <span className="chat-conversation-row-label">
            {running ? (
              <span className="chat-conversation-running-indicator" aria-hidden>
                <ActivityIndicator size="small" />
              </span>
            ) : null}
            <Text
              variant="body-sm"
              className={cn('truncate text-left', active || running ? 'font-medium' : '')}
            >
              {conversationLabel(conversation)}
            </Text>
          </span>
        </Pressable>
        <button
          ref={menuButtonRef}
          type="button"
          className="chat-conversation-menu-btn"
          aria-label="Conversation actions"
          title={pinned ? 'Pinned' : undefined}
          onClick={(event) => {
            event.stopPropagation();
            if (menuOpen) {
              closeMenu();
            } else {
              openMenuAtButton();
            }
          }}
        >
          ⋯
        </button>
      </div>

      <Menu open={menuOpen} anchor={menuAnchor} onClose={closeMenu} minWidth={180}>
        {archived ? (
          <>
            <MenuItem label="Restore" onPress={onRestore} />
            <MenuItem destructive label="Delete" onPress={() => void onDelete()} />
          </>
        ) : (
          <>
            <MenuItem label={pinned ? 'Unpin' : 'Pin'} onPress={onPin} />
            <MenuItem label="Fork" onPress={() => void onFork()} />
            <MenuItem label="Rename" onPress={onRename} />
            <MenuItem destructive label="Delete" onPress={() => void onDelete()} />
            <MenuItem label="Archive" onPress={onArchive} />
          </>
        )}
      </Menu>
    </>
  );
}
